import React, {useEffect} from 'react';
import {BrowserRouter as Router, Navigate, Route, Routes, useLocation} from 'react-router-dom'
import classNames from "classnames";
import {useDispatch, useSelector} from 'react-redux';
import {Provider} from 'react-redux';
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import translations from "./translations";
import {Navbar} from "./components/Navbar";
import HomePage from "./pages/Home";
import Demo from "./pages/Demo";
import Modals from "./components/Modals";
import {createAppStore} from "./store";
import reducer from './store/reducer';
import StakePage from "./pages/Stake";
import {history} from "./utils/history";
import toast, {Toaster} from 'react-hot-toast';
import ClaimPage from "./pages/Claim";
import PrivacyPage from "./pages/Privacy";
import NewsPage from "./pages/News";
import PodcastPage from "./pages/Podcast";
import Wallet from "./store/models/wallet";

const initialState = {};
const store = createAppStore(reducer, initialState);

const defaultLanguage = 'en'; // The site is English-only

i18n
    .use(initReactI18next)
    .init({
        resources: translations,
        lng: defaultLanguage,
        fallbackLng: defaultLanguage,
        interpolation: {
            escapeValue: false
        }
    });

const withNavBar = (Component) => () => (
    <React.Fragment>
      <Navbar/>
      <Component/>
    </React.Fragment>
);

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }

    const targetId = decodeURIComponent(hash.replace('#', ''));
    let frameId = null;
    let timerId = null;

    const scrollToTarget = () => {
      const target = document.getElementById(targetId);

      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        return true;
      }

      return false;
    };

    frameId = window.requestAnimationFrame(() => {
      if (!scrollToTarget()) {
        timerId = window.setTimeout(scrollToTarget, 120);
      }
    });

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }

      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [pathname, hash]);

  return null;
}

function App() {
    return (
      <Provider store={store}>
          <Router historu={history}>
              <div className={classNames('App')}>
              <ScrollToTop/>
              <Routes>
                  <Route exact path="/" element={withNavBar(HomePage)()}/>
                  <Route exact path="/about" element={<Navigate to="/#about" replace />} />
                  <Route exact path="/platform" element={<Navigate to="/#overview" replace />} />
                  <Route exact path="/components" element={<Navigate to="/#key-components" replace />} />
                  <Route exact path="/docs" element={<Navigate to="/#docs" replace />} />
                  <Route exact path="/roadmap" element={<Navigate to="/#roadmap" replace />} />
                  <Route exact path="/apps" element={<Navigate to="/#apps" replace />} />
                  <Route exact path="/demo" element={withNavBar(Demo)()}/>
                  <Route exact path="/apps/:app-slug" element={<Navigate to="/#apps" replace />} />
                  <Route exact path="/stake" element={withNavBar(StakePage)()}/>
                  <Route exact path="/claim" element={withNavBar(ClaimPage)()}/>
                  <Route exact path="/news" element={withNavBar(NewsPage)()}/>
                  <Route exact path="/podcast" element={withNavBar(PodcastPage)()}/>
                  <Route exact path="/privacy" element={withNavBar(PrivacyPage)()}/>
                  {/* default redirect to home page */}
                  <Route path="*" element={<Navigate to="/" />} />
              </Routes>
              </div>
              <Toaster containerStyle={{
                  zIndex: 9999999999
              }}
                       toastOptions={{
                           success: {
                               style: {
                                   background: 'white',
                                   border: '1px solid #white',
                                   color: '#202224'
                               },
                               iconTheme: {
                                   primary: '#34E393',
                                   secondary: 'white',
                               },
                           },
                           error: {
                               style: {
                                   background: 'white',
                                   border: '1px solid white',
                                   color: '#202224'
                               },
                               iconTheme: {
                                   primary: '#f05555',
                                   secondary: 'white',
                               },
                           },
                       }}/>
              <Modals/>
          </Router>
      </Provider>
  );
}

export default App;
